import { useAction } from 'convex/react';
import { useQuery } from '@tanstack/react-query';
import { convexQuery } from '@convex-dev/react-query';
import { api } from '@/convex/_generated/api';
import { useEffect, useRef, useState } from 'react';
import { registerIdentityScopedSessionClear } from '@/lib/identity-scoped-session-memory';

// Share-scope cache, keyed by `${token}|${storageKey}` so URLs resolved
// through one share token can never bleed into another scope (or into the
// member memory, which is scoped to a confirmed identity).
const shareUrlCache: Map<string, string | null> = new Map();
// Signed-in viewers read shares too, so it is dropped on every confirmed-
// identity change like the member memory (#4628).
registerIdentityScopedSessionClear(() => shareUrlCache.clear());

// One public-copy trigger per share token + workspaceFileId across all hook
// instances. Released only on failure, so the delayed retry can fire again; a
// ready, pending or unavailable answer stays recorded for the tab.
const pendingPublicCopyTriggers = new Set<string>();

// Delay before retrying a failed public-copy trigger.
const PUBLIC_COPY_RETRY_DELAY_MS = 5_000;

/**
 * Share-scope token-gated resolution (#4716): inside a thread share the share
 * decides first, for every viewer. The query admits only files the share
 * exposes: it returns a public URL for an anonymous viewer (or
 * `publicCopyPending` + workspaceFileId, in which case we fire the idempotent
 * copy trigger and let Convex reactivity deliver the URL), and echoes the
 * storage key for a signed-in grantee, which the member resolver then
 * authorizes for that user (`memberAdmitted`). A key the share does not expose
 * stays unavailable even when the viewer could read it elsewhere.
 */
export function useSharedStorageUrl({
  shareToken,
  storageKey,
}: {
  /** The share token when rendering inside a thread share, else null. */
  shareToken: string | null;
  storageKey: string | null;
}): {
  shareCacheKey: string | null;
  url: string | null;
  /** Outside a share always true; inside, only for the key the share echoed. */
  memberAdmitted: boolean;
  isLoading: boolean;
} {
  const shareCacheKey =
    shareToken && storageKey ? `${shareToken}|${storageKey}` : null;
  const cachedShareUrl = shareCacheKey
    ? shareUrlCache.get(shareCacheKey)
    : undefined;
  const shouldQueryShare =
    shareCacheKey !== null && typeof cachedShareUrl !== 'string';

  const { data: shareData } = useQuery({
    ...convexQuery(
      api.organizations.threads.shared_access.resolveSharedThreadMediaUrls,
      shouldQueryShare && shareToken && storageKey
        ? {
            token: shareToken,
            refs: [{ kind: 'storageKey' as const, storageKey }],
          }
        : 'skip'
    ),
    enabled: shouldQueryShare,
  });
  const shareResult = shareData?.[0];
  const memberAdmitted =
    shareToken === null ||
    (storageKey !== null && shareResult?.storageKey === storageKey);

  useEffect(() => {
    if (shareCacheKey && shareResult?.url) {
      shareUrlCache.set(shareCacheKey, shareResult.url);
    }
  }, [shareCacheKey, shareResult?.url]);

  const ensureSharedThreadFilePublicCopy = useAction(
    api.organizations.threads.thread_share_public_copies_actions
      .ensureSharedThreadFilePublicCopy
  );
  // On a failed trigger (transient network/server error) none of the effect's
  // other deps change — `publicCopyPending` stays true — so bump a retry tick
  // after a delay or the viewer would be stuck with blank media until reload.
  const [copyRetryTick, setCopyRetryTick] = useState(0);
  const copyRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (copyRetryTimerRef.current !== null) {
        clearTimeout(copyRetryTimerRef.current);
        copyRetryTimerRef.current = null;
      }
    };
  }, []);
  useEffect(() => {
    const pendingFileId = shareResult?.publicCopyPending
      ? shareResult.workspaceFileId
      : undefined;
    if (!pendingFileId || !shareToken) return;
    const triggerKey = `${shareToken}|${String(pendingFileId)}`;
    if (pendingPublicCopyTriggers.has(triggerKey)) return;
    pendingPublicCopyTriggers.add(triggerKey);
    // The action reports a failed copy as a resolved `{ status: 'failed' }`,
    // so that counts as a failure too (#4716).
    const fail = () => {
      pendingPublicCopyTriggers.delete(triggerKey);
      if (copyRetryTimerRef.current === null) {
        copyRetryTimerRef.current = setTimeout(() => {
          copyRetryTimerRef.current = null;
          setCopyRetryTick((tick) => tick + 1);
        }, PUBLIC_COPY_RETRY_DELAY_MS);
      }
    };
    void ensureSharedThreadFilePublicCopy({
      token: shareToken,
      workspaceFileId: pendingFileId,
    }).then((result) => {
      if (result?.status === 'failed') fail();
    }, fail);
  }, [
    ensureSharedThreadFilePublicCopy,
    shareResult?.publicCopyPending,
    shareResult?.workspaceFileId,
    shareToken,
    copyRetryTick,
  ]);

  const url =
    typeof cachedShareUrl === 'string'
      ? cachedShareUrl
      : (shareResult?.url ?? null);
  const isLoading =
    shareCacheKey !== null &&
    ((shouldQueryShare && shareData === undefined) ||
      shareResult?.publicCopyPending === true);
  return { shareCacheKey, url, memberAdmitted, isLoading };
}
